import type { QueryClient } from '@tanstack/react-query';
import type { LOAN_REQUEST_OFFER_SOURCE, OFFER_ACCEPTANCE_STATUS } from 'cpool-constants';
import { AGGREGATE_NAMES, LOAN_REQUEST_RESOLVERS, OFFER_ACCEPTANCE_STATUSES } from 'cpool-constants';
import type { SchemaLoanRequestResolver_GetOfferAcceptanceStatusByOfferId } from 'cpool-tables/backend-resolvers';
import { schemaLoanRequestResolver_GetOfferAcceptanceStatusByOfferId } from 'cpool-tables/backend-resolvers';
import { sleep } from 'cpool-utils';
import { type TFunction } from 'i18next';
import { toast } from 'react-toastify';

import { getQueryOptions } from '~/queryUtils/getQueryOptions';
import { queryKeys } from '~/utils/queryKeys';

const WAIT_TIME = 1000;

type QueryOptionsProps = {
    loanRequestId: string;
    offerId: string;
    source: LOAN_REQUEST_OFFER_SOURCE;
};

const getOfferAcceptanceStatusByOfferIdQueryOptions = ({ loanRequestId, offerId, source }: QueryOptionsProps) =>
    getQueryOptions({
        aggregateName: AGGREGATE_NAMES.LOAN_REQUEST,
        resolverName: LOAN_REQUEST_RESOLVERS.getOfferAcceptanceStatusByOfferId,
        schema: schemaLoanRequestResolver_GetOfferAcceptanceStatusByOfferId,
        searchParams: { id: loanRequestId, offerId, source },
    });

type Props = {
    loanRequestId: string;
    offerId: string;
    source: LOAN_REQUEST_OFFER_SOURCE;
    waitTime?: number;
    queryClient: QueryClient;
    t: TFunction<'de' | 'en', undefined>;
};
export const getAcceptanceStatusByOfferId = async ({
    loanRequestId,
    offerId,
    queryClient,
    source,
    t,
    waitTime = WAIT_TIME,
}: Props) => {
    try {
        let offerAcceptanceStatus: OFFER_ACCEPTANCE_STATUS = OFFER_ACCEPTANCE_STATUSES.PENDING;
        let response: SchemaLoanRequestResolver_GetOfferAcceptanceStatusByOfferId = null;
        while (offerAcceptanceStatus === OFFER_ACCEPTANCE_STATUSES.PENDING) {
            // eslint-disable-next-line no-await-in-loop
            await sleep(waitTime);
            // eslint-disable-next-line no-await-in-loop
            response = await queryClient.fetchQuery(
                getOfferAcceptanceStatusByOfferIdQueryOptions({ loanRequestId, offerId, source }),
            );

            offerAcceptanceStatus = response || OFFER_ACCEPTANCE_STATUSES.PENDING;
        }
        if (offerAcceptanceStatus === OFFER_ACCEPTANCE_STATUSES.SUCCESS) {
            queryClient.invalidateQueries({
                queryKey: queryKeys.getAcceptedOffers(loanRequestId),
            });
        }
    } catch {
        toast.error(t('BANK_CREDIT_REQUEST.OFFERS_TABLE.OFFER_ACCEPTANCE_FAILED'));
    }
};
